import { Bell, BellOff } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { ALERT_SYMS, useMarket } from "@/context/MarketContext";

export default function AlertSettings() {
  const { soundOn, toggleSound, alertCfg, setAlertCfg, testAlert, quotes } = useMarket();
  const toggleSym = (s) => setAlertCfg({ ...alertCfg, symbols: alertCfg.symbols.includes(s) ? alertCfg.symbols.filter((x) => x !== s) : [...alertCfg.symbols, s] });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="sound-alert-toggle" aria-label="Signal alert settings" title={soundOn ? `Alerts on at ${alertCfg.level}` : "Alerts muted"}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-colors ${soundOn ? "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/10 shadow-[0_0_14px_rgba(0,255,102,.3)]" : "text-gray-500 border-white/10 hover:text-white"}`}>
          {soundOn ? <Bell size={14} /> : <BellOff size={14} />}
          <span className="font-data text-[11px] font-bold" data-testid="alert-level-chip">{alertCfg.level}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#11151C]/95 backdrop-blur-xl border-white/10 p-5" data-testid="alert-settings-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-bold">Signal alerts</div>
            <div className="text-[11px] text-gray-500">Chime + flash when an index crosses your level</div>
          </div>
          <Switch checked={soundOn} onCheckedChange={toggleSound} data-testid="alert-sound-switch" />
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-xs mb-2"><span className="text-gray-400">Alert level (Strength Score)</span><span className="font-data font-bold text-[#00FF66]" data-testid="alert-level-value">{alertCfg.level}</span></div>
          <Slider min={55} max={95} step={1} value={[alertCfg.level]} onValueChange={([v]) => setAlertCfg({ ...alertCfg, level: v })} data-testid="alert-level-slider" />
          <div className="flex justify-between gap-1 mt-3">
            {[65, 70, 75, 80, 85].map((v) => (
              <button key={v} onClick={() => setAlertCfg({ ...alertCfg, level: v })} data-testid={`alert-preset-${v}`}
                className={`flex-1 py-1 rounded-md text-[11px] font-data border transition-colors ${alertCfg.level === v ? "border-[#00FF66]/50 text-[#00FF66] bg-[#00FF66]/10" : "border-white/10 text-gray-400 hover:text-white"}`}>{v}</button>
            ))}
          </div>
        </div>
        <div className="mt-5">
          <div className="text-xs text-gray-400 mb-2">Indices that trigger alerts</div>
          <div className="grid grid-cols-2 gap-2">
            {ALERT_SYMS.map((s) => {
              const on = alertCfg.symbols.includes(s);
              return (
                <button key={s} onClick={() => toggleSym(s)} data-testid={`alert-index-${s}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs transition-colors ${on ? "border-[#007AFF]/50 bg-[#007AFF]/10 text-white" : "border-white/10 text-gray-500"}`}>
                  <span className="font-semibold">{s}</span><span className="font-data">{quotes[s]?.score?.toFixed(0) ?? "—"}</span>
                </button>
              );
            })}
          </div>
        </div>
        <button onClick={() => testAlert("NIFTY")} data-testid="alert-test-btn" className="mt-5 w-full rounded-full border border-white/15 py-2 text-xs font-semibold hover:border-[#00FF66]/50 hover:text-[#00FF66] transition-colors">Play test alert</button>
      </PopoverContent>
    </Popover>
  );
}
